/* component-test-check-ignore */
import React, { useEffect, useRef, useState } from 'react';

import { Box, Button, Icon, Text } from '@opendoor/bricks/core';

/**
 * A paragraph friendly, lightweight tooltip icon with popover
 *
 * Compared to the Brick's Tooltip component, this one is:
 * - positioned above the icon instead of adjustable
 * - always triggered on click, and does not support hover trigger
 *   - supporting hover would be significantly more complex because cannot
 *     use a button element if hover trigger due to a11y/a360. See slack
 *     conversation here: https://go/slack-toolips
 * - much less code/javascript
 * - relative positioning instead of absolute (makes it sometimes more reliable, like when in a scroll region)
 * - does not inject any divs, so doesn't throw HTML warnings
 */
export default function ParagraphTooltip({
  analyticsName,
  children,
}: {
  analyticsName: string;
  children: React.ReactNode;
}) {
  const [showPopover, setShowPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const width = 270;
  const arrowBoxHeight = 14;
  const padding = 16;

  useEffect(function movePopoverUp() {
    const popover = popoverRef.current;
    if (!popover) return;
    popover.style.top = `-${popover.clientHeight + arrowBoxHeight}px`;
  }, []);

  return (
    <Box as="span" position="relative">
      <Button
        analyticsName={analyticsName}
        aria-label="tooltip"
        display="inline-block"
        onClick={(e) => {
          e.stopPropagation();
          setShowPopover((last) => !last);
        }}
        type="button"
        variant="ghost"
      >
        <Icon mb="-2px" name="info-filled" size={16} color="warmgrey800" />
      </Button>
      <Box
        aria-hidden={showPopover ? 'false' : 'true'}
        as="span"
        borderRadius="roundedSquare"
        bgColor="warmgrey950"
        display="inline-block"
        left={-1 * (width / 2 - 17 / 2)} // 17 = size of tooltip icon
        padding={padding}
        position="absolute"
        ref={popoverRef}
        role="tooltip"
        visibility={showPopover ? 'visible' : 'hidden'}
        w={width}
        zIndex={1000}
      >
        <Text color="white0" display="block" size="90" textAlign="left">
          {children}
        </Text>
        <Box
          as="span"
          borderLeft={`${arrowBoxHeight}px solid transparent`}
          borderRight={`${arrowBoxHeight}px solid transparent`}
          borderTop={`${arrowBoxHeight}px solid var(--colors-warmgrey950)`}
          bottom={-1 * arrowBoxHeight}
          display="block"
          left={`calc(50% - ${arrowBoxHeight}px)`}
          h={0}
          position="absolute"
          w={0}
        />
      </Box>
    </Box>
  );
}
